package ai.jessie.web.members

import ai.jessie.shared.members.ChangeMemberRoleRequest
import ai.jessie.shared.members.InvitationChangeRequest
import ai.jessie.shared.members.InvitationResponse
import ai.jessie.shared.members.IssueInvitationRequest
import ai.jessie.shared.members.MemberChangeResponse
import ai.jessie.shared.members.MemberManagementResponse
import ai.jessie.shared.members.MemberResponse
import ai.jessie.shared.members.RemoveMemberRequest
import ai.jessie.web.onboarding.OnboardingProblem
import ai.jessie.web.onboarding.OnboardingRequestFailure
import ai.jessie.web.onboarding.unavailableProblem
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.currentCoroutineContext
import kotlinx.coroutines.ensureActive
import kotlinx.coroutines.launch

// UC-005 step 3. A chosen change waits for confirmation (steps 4–5) before any request.
sealed interface MemberChange {
    data class Issue(val email: String, val role: String) : MemberChange

    data class Resend(val invitation: InvitationResponse) : MemberChange

    data class Revoke(val invitation: InvitationResponse) : MemberChange

    data class ChangeRole(val member: MemberResponse, val role: String) : MemberChange

    data class Remove(val member: MemberResponse) : MemberChange
}

data class MemberManagementUiState(
    val management: MemberManagementResponse? = null,
    val email: String = "",
    val role: String = "STANDARD",
    val pending: MemberChange? = null,
    val completed: MemberChange? = null,
    // A3: the pending invitation that already covers the requested address.
    val existingInvitationId: String? = null,
    // The actor's own change ended their owner authority; owner data is dropped, never
    // re-requested.
    val authorityEnded: Boolean = false,
    val busy: Boolean = false,
    val error: OnboardingProblem? = null,
)

data class MemberManagementActions(
    val refresh: () -> Unit,
    val email: (String) -> Unit,
    val role: (String) -> Unit,
    val choose: (MemberChange) -> Unit,
    val invite: () -> Unit,
    val confirm: () -> Unit,
    val cancel: () -> Unit,
)

class MemberManagementState(
    private val port: MemberManagementPort,
    private val scope: CoroutineScope,
    private val organisationId: String,
) {
    var state by mutableStateOf(MemberManagementUiState())
        private set

    val actions =
        MemberManagementActions(
            refresh = ::refresh,
            email = { if (!state.busy) state = state.copy(email = it) },
            role = { if (!state.busy && it in MEMBER_ROLES) state = state.copy(role = it) },
            choose = ::choose,
            invite = { choose(MemberChange.Issue(state.email.trim(), state.role)) },
            confirm = ::confirm,
            // A7: cancelling sends no request and changes nothing.
            cancel = { if (!state.busy) state = state.copy(pending = null) },
        )

    fun refresh() {
        if (state.authorityEnded) return
        request(keepPending = false) {
            val management = port.members(organisationId)
            currentCoroutineContext().ensureActive()
            state = state.copy(management = management, pending = null)
        }
    }

    // Only entries of the current listing can be chosen, so tokens are always the listed ones.
    private fun choose(change: MemberChange) {
        val listing = state.management
        if (state.busy || state.authorityEnded || listing == null) return
        val listed =
            when (change) {
                is MemberChange.Issue -> true
                is MemberChange.Resend -> change.invitation in listing.invitations
                is MemberChange.Revoke -> change.invitation in listing.invitations
                is MemberChange.ChangeRole ->
                    change.member in listing.members &&
                        change.role in MEMBER_ROLES &&
                        change.role != change.member.role
                is MemberChange.Remove -> change.member in listing.members
            }
        if (listed)
            state =
                state.copy(
                    pending = change,
                    completed = null,
                    existingInvitationId = null,
                    error = null,
                )
    }

    private fun confirm() {
        val change = state.pending ?: return
        request(keepPending = true) {
            val response = send(change)
            currentCoroutineContext().ensureActive()
            val management = response.management
            state =
                state.copy(
                    management = management,
                    authorityEnded = management == null,
                    pending = null,
                    completed = change,
                    email = if (change is MemberChange.Issue) "" else state.email,
                )
        }
    }

    private suspend fun send(change: MemberChange): MemberChangeResponse =
        when (change) {
            is MemberChange.Issue ->
                port.issue(organisationId, IssueInvitationRequest(change.email, change.role))
            is MemberChange.Resend ->
                port.resend(
                    organisationId,
                    change.invitation.invitationId,
                    change.invitation.expected(),
                )
            is MemberChange.Revoke ->
                port.revoke(
                    organisationId,
                    change.invitation.invitationId,
                    change.invitation.expected(),
                )
            is MemberChange.ChangeRole ->
                port.changeRole(
                    organisationId,
                    change.member.memberId,
                    ChangeMemberRoleRequest(change.role, change.member.role, change.member.version),
                )
            is MemberChange.Remove ->
                port.remove(
                    organisationId,
                    change.member.memberId,
                    RemoveMemberRequest(change.member.role, change.member.version),
                )
        }

    // Single-flight; disposed page scopes cannot publish late success, error or finalisation.
    private fun request(keepPending: Boolean, block: suspend () -> Unit) {
        if (state.busy) return
        state =
            state.copy(
                busy = true,
                error = null,
                completed = null,
                existingInvitationId = null,
                pending = state.pending.takeIf { keepPending },
            )
        scope.launch {
            try {
                block()
            } catch (cancelled: CancellationException) {
                throw cancelled
            } catch (failure: OnboardingRequestFailure) {
                currentCoroutineContext().ensureActive()
                rejected(failure)
            } catch (_: Exception) {
                currentCoroutineContext().ensureActive()
                state = state.copy(error = unavailableProblem())
            } finally {
                currentCoroutineContext().ensureActive()
                state = state.copy(busy = false)
            }
        }
    }

    private suspend fun rejected(failure: OnboardingRequestFailure) {
        val requestedEmail = (state.pending as? MemberChange.Issue)?.email
        state =
            state.copy(
                // A1: a denial leaves no membership information on the page.
                management = state.management.takeUnless { failure.deniesAccess() },
                // A6 continues at step 5: only an uncompleted change stays ready to resubmit.
                pending = state.pending.takeIf { failure.notCompleted() },
                error = failure.memberProblem(),
            )
        if (!failure.reloadsListing()) return
        // A3 and A5: show the current members, roles and pending invitations.
        try {
            val management = port.members(organisationId)
            currentCoroutineContext().ensureActive()
            state =
                state.copy(
                    management = management,
                    existingInvitationId =
                        management.invitations
                            .firstOrNull {
                                failure.code == "INVITATION_EXISTS" &&
                                    it.email.equals(requestedEmail, ignoreCase = true)
                            }
                            ?.invitationId,
                )
        } catch (cancelled: CancellationException) {
            throw cancelled
        } catch (reload: OnboardingRequestFailure) {
            currentCoroutineContext().ensureActive()
            state = state.copy(management = null, error = reload.memberProblem())
        } catch (_: Exception) {
            currentCoroutineContext().ensureActive()
            state = state.copy(management = null, error = unavailableProblem())
        }
    }
}

private fun InvitationResponse.expected() = InvitationChangeRequest(status, expiresAt)

internal val MEMBER_ROLES = listOf("OWNER", "STANDARD", "ACCOUNTANT")
